import React, { useRef, useEffect } from "react";
import { debounce, isBoolean } from "lodash";

type Props = {
  placeholder: string;
  value: string;
  searchCallback(query: string): void;
  onChange(query: string): void;
  onClear(): void;
  onFocus?: () => void;
  focused?: boolean;
};

const Search = ({
  placeholder,
  value,
  searchCallback,
  onChange,
  onClear,
  onFocus,
  focused,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchCallback = debounce(searchCallback, 300);

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const query = evt.target.value || "";
    onChange(query);
    debouncedSearchCallback(query);
  };

  const handleFocus = () => {
    if (onFocus) onFocus();
  };

  // focus and blur text input given value of focused
  useEffect(() => {
    if (inputRef.current && isBoolean(focused)) {
      if (focused) {
        inputRef.current.focus();
      } else {
        inputRef.current.blur();
      }
    }
  }, [focused, inputRef]);

  return (
    <form className="search" onSubmit={(evt) => evt.preventDefault()}>
      <label htmlFor="search-input">
        <input
          id="search-input"
          className="search"
          ref={inputRef}
          value={value}
          title={placeholder}
          placeholder={placeholder}
          onChange={handleChange}
          onClick={(evt) => evt.nativeEvent.stopImmediatePropagation()}
          onFocus={handleFocus}
        />
        <div className="search-magnifier" />
        {value.length ? (
          <div
            className="search-clear"
            role="button"
            tabIndex={0}
            onClick={onClear}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClear();
              }
            }}
            aria-label="Clear search"
          />
        ) : null}
      </label>
    </form>
  );
};

export default Search;
